/**
 * Text and numeric inputs.
 *
 * `NumberInput` exists as a separate component because `<input type="number">` has
 * three behaviours that are actively dangerous in a structural calculation form:
 *
 * 1. **The mouse wheel changes the value** while the field has focus. An engineer
 *    scrolling down a long input form silently edits whatever they last clicked. This
 *    is the single most common way bad numbers enter engineering web forms, and the
 *    edit looks exactly like one they made on purpose.
 * 2. **Up/down arrows change the value** when the user meant to move the caret.
 * 3. A partially-typed value (`"6."`, `"-"`) parses as `NaN`, so binding straight to a
 *    number makes the field impossible to type in.
 *
 * So: wheel is blocked while focused, the raw string is held during editing and only
 * committed as a number when it parses, and the decimal separator is always `.`
 * (ADR-0004) with `inputMode="decimal"` for a sane mobile keypad.
 */
import { forwardRef, useEffect, useRef, useState, type InputHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'
import { useFieldControlProps } from '@/components/ui/field'

const baseClass =
  'h-11 w-full rounded-md border border-border bg-surface px-3 text-text ' +
  'placeholder:text-text-muted focus-visible:outline-2 focus-visible:outline-offset-1 ' +
  'disabled:opacity-50 aria-[invalid=true]:border-[var(--status-fail)]'

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(baseClass, className)} {...props} />
})

/** As `Input`, but wired to the surrounding `<Field>` for label and error association. */
export const FieldInput = forwardRef<HTMLInputElement, InputProps>(function FieldInput(props, ref) {
  const fieldProps = useFieldControlProps()
  return <Input ref={ref} {...fieldProps} {...props} />
})

export interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> {
  value: number
  onValueChange: (value: number) => void
  /** Arrow-key and stepper increment. Wheel is always disabled. */
  step?: number
  min?: number
  max?: number
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, step = 1, min, max, className, ...props },
  forwardedRef,
) {
  const localRef = useRef<HTMLInputElement | null>(null)
  const fieldProps = useFieldControlProps()

  // The raw string is what the user is typing; `value` is what has been committed.
  // Without this, "6." collapses to "6" mid-keystroke and the caret jumps.
  const [draft, setDraft] = useState<string>(() => String(value))
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    // Adopt an external change (a preset, an undo) unless the user is mid-edit --
    // overwriting someone's half-typed number is worse than a moment's staleness.
    if (!editing) setDraft(String(value))
  }, [value, editing])

  /**
   * Block wheel-to-change without blocking page scroll.
   *
   * A passive listener cannot preventDefault, and React's onWheel is passive by
   * default, so this is attached natively. Blurring instead of preventing keeps the
   * page scrolling normally under the cursor -- the field just stops eating the event.
   */
  useEffect(() => {
    const element = localRef.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      if (document.activeElement === element) {
        event.preventDefault()
        element.blur()
      }
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [])

  const commit = (raw: string) => {
    setDraft(raw)
    // An empty or partial value is not an error, it is an unfinished keystroke.
    if (raw === '' || raw === '-' || raw.endsWith('.')) return
    const parsed = Number(raw)
    if (Number.isFinite(parsed)) onValueChange(parsed)
  }

  return (
    <input
      ref={(node) => {
        localRef.current = node
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      }}
      type="text"
      // Numeric keypad on mobile. `type="text"` plus inputMode avoids the wheel and
      // spinner behaviour of type="number" while keeping the right keyboard (14.3).
      inputMode="decimal"
      // Only digits, a leading minus, and a dot: the decimal separator is never a
      // comma, in any locale (ADR-0004).
      pattern="-?[0-9]*\.?[0-9]*"
      autoComplete="off"
      value={draft}
      onFocus={() => setEditing(true)}
      onChange={(event) => commit(event.target.value)}
      onBlur={() => {
        setEditing(false)
        const parsed = Number(draft)
        // Snap back to the last good value rather than leaving a field reading "6.".
        setDraft(Number.isFinite(parsed) && draft !== '' ? String(parsed) : String(value))
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
        event.preventDefault()
        const direction = event.key === 'ArrowUp' ? 1 : -1
        const next = (Number.isFinite(value) ? value : 0) + direction * step
        const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, next))
        // Floating-point noise: 0.1 + 0.2 must not put 0.30000000000000004 in a form.
        const rounded = Number(clamped.toPrecision(12))
        setDraft(String(rounded))
        onValueChange(rounded)
      }}
      className={cn(baseClass, 'tabular', className)}
      {...fieldProps}
      {...props}
    />
  )
})
